import React, {
  memo,
  useMemo,
  Fragment,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useDropzone } from "react-dropzone";
import { styled, alpha } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import formatBytes from "../../utils/formatBytes";

const getColor = ({ isDragAccept, isDragReject, isFocused, theme }) => {
  if (isDragAccept) {
    return alpha("#00e676", 0.8);
  }
  if (isDragReject) {
    return alpha("#ff1744", 0.8);
  }
  if (isFocused) {
    return alpha(theme.palette.primary.main, 0.4);
  }
  return "#00000061";
};

const LargeFileMessage = styled("div")(({ theme }) => ({
  color: theme.palette.error.main,
  fontWeight: 600,
}));

const Container = styled("div")(
  ({ isDragAccept, isDragReject, isFocused, theme }) => ({
    flex: 1,
    display: "flex",
    cursor: "pointer",
    flexDirection: "column",
    alignItems: "center",
    padding: 20,
    borderWidth: 3,
    borderRadius: 3,
    borderColor: getColor({
      isDragAccept,
      isDragReject,
      isFocused,
      theme,
    }),
    borderStyle: "dashed",
    backgroundColor: "#80808017",
    color: theme.palette.grey[600],
    outline: "none",
    transition: "border 0.24s ease-in-out",
    marginBottom: 10,
    "&.disabled": {
      opacity: "0.6",
      cursor: "default",
    },
  })
);

const selectedFileChipSx = {
  color: (theme) => alpha(theme.palette.primary.main, 0.8),
  fontWeight: 600,
  "& .MuiChip-deleteIcon": {
    color: (theme) => alpha(theme.palette.primary.main, 0.5),
    "&:hover": {
      color: (theme) => alpha(theme.palette.primary.main, 0.8),
    },
  },
};

// Bytes binary
const UPPER_FILE_SIZE_LIMIT = 52428800;

const FileUpload = (props) => {
  const {
    onUploadFile,
    uploading = false,
    disabled = false,
    getFileData = () => {},
    isDataguide = false,
  } = props;
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isFileTooLarge, setIsFileTooLarge] = useState(false);
  const [isFileInvalid, setIsFileInvalid] = useState(false);

  useEffect(() => {
    getFileData(selectedFiles);
  }, [getFileData, selectedFiles]);

  const disabledDropzone = useMemo(
    () => uploading || disabled,
    [uploading, disabled]
  );

  const onDrop = useCallback((acceptedFiles) => {
    setIsFileTooLarge(false);
    setIsFileInvalid(false);
    setSelectedFiles(acceptedFiles);
  }, []);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      accept: {
        ...(!isDataguide && {
          "application/vnd.ms-excel": [],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            [],
          "text/csv": [],
        }),
      },
      disabled: disabledDropzone,
      maxFiles: 1,
      minSize: 0,
      maxSize: UPPER_FILE_SIZE_LIMIT, //50MB = 50000000 //10KB = 10000
      onDrop,
      onDropRejected: (files) => {
        if (files[0]?.errors[0].code === "file-too-large") {
          setIsFileTooLarge(true);
        } else if (files[0]?.errors[0].code === "file-invalid-type") {
          setIsFileInvalid(true);
        } else {
          setIsFileInvalid(false);
          setIsFileTooLarge(false);
        }
      },
    });

  const handleClearSelection = useCallback(() => {
    if (uploading) {
      return;
    }
    return setSelectedFiles([]);
  }, [uploading]);

  const handleFileUpload = useCallback(() => {
    onUploadFile(selectedFiles);
  }, [selectedFiles, onUploadFile]);

  const acceptedFileItems = useMemo(
    () =>
      !!selectedFiles.length ? (
        selectedFiles.map((file) => (
          <Fragment key={file.path}>
            Selected File:&nbsp;
            <Chip
              variant="outlined"
              label={file.path}
              onDelete={handleClearSelection}
              size="small"
              sx={selectedFileChipSx}
              deleteIcon={<DeleteIcon />}
              disabled={disabled}
            />
            &nbsp;
            <Tooltip title={uploading ? "Uploading" : "Upload"}>
              <IconButton
                sx={{
                  color: (theme) => alpha(theme.palette.primary.main, 0.5),
                  "&:hover": {
                    color: (theme) => alpha(theme.palette.primary.main, 0.8),
                  },
                }}
                onClick={handleFileUpload}
                disabled={uploading || disabled}
              >
                <CloudUploadOutlined />
              </IconButton>
            </Tooltip>
          </Fragment>
        ))
      ) : (
        <Box sx={{ display: "inline-flex", pt: 1, pb: 1 }}>
          Selected File:&nbsp;
          <Chip
            variant="outlined"
            label="No File Selected"
            size="small"
            sx={{ color: (theme) => theme.palette.grey[600] }}
          />
        </Box>
      ),
    [selectedFiles, uploading, handleFileUpload, handleClearSelection, disabled]
  );

  return (
    <section className="container">
      <Container
        {...getRootProps({
          isFocused,
          isDragAccept,
          isDragReject,
          className: disabledDropzone ? "disabled" : "",
        })}
      >
        <input {...getInputProps()} />
        <CloudUploadOutlined sx={{ fontSize: 40 }} />
        <Typography variant="subtitle1">
          Drag 'n' drop a file here, or click to select a file
        </Typography>
        {isFileTooLarge && (
          <LargeFileMessage>
            This file is too large to be uploaded. File larger than&nbsp;
            {formatBytes(UPPER_FILE_SIZE_LIMIT)} is not currently supported.
          </LargeFileMessage>
        )}
        {isFileInvalid && (
          <LargeFileMessage>Invalid file format.</LargeFileMessage>
        )}
      </Container>
      <aside>
        <Typography variant="subtitle2">{acceptedFileItems}</Typography>
      </aside>
    </section>
  );
};

export default memo(FileUpload);
